import type { Bootstrap, Service } from '../types/storefront'

export type Currency = Bootstrap['currency']

/** Non-breaking space: a price must never wrap between the number and its symbol. */
const NBSP = ' '

/** Grouping follows the vendor currency, not the browser locale, so the same
 *  booking shows the same number to a customer on any device. */
export function formatPrice(amount: number, currency: Currency): string {
  const locale = currency.code === 'VND' ? 'vi-VN' : 'en-US'
  const body = amount.toLocaleString(locale, { maximumFractionDigits: 2 })
  return currency.position === 'left' ? `${currency.symbol}${body}` : `${body}${NBSP}${currency.symbol}`
}

/** A bill amount, always to the cent ("$45.00") the way the salon's receipt prints it. */
export function formatMoney(amount: number, currency: Currency): string {
  const locale = currency.code === 'VND' ? 'vi-VN' : 'en-US'
  const digits = currency.code === 'VND' ? 0 : 2
  const body = amount.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })
  return currency.position === 'left' ? `${currency.symbol}${body}` : `${body}${NBSP}${currency.symbol}`
}

/** 75 -> "1h 15m", 45 -> "45m", 120 -> "2h". */
export function formatDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0m'
  const hours = Math.floor(minutes / 60)
  const rest = Math.round(minutes % 60)
  if (!hours) return `${rest}m`
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

/** The amount a customer will actually be charged for one service. */
export function effectivePrice(service: Service): number {
  return service.discounted_price ?? service.price
}
